import { ResourceResponse, ThunkReturnType } from "types";
import type { PayloadAction, WritableDraft } from "infrastructure/store";
import { ApiState } from "infrastructure/api";
import { Paginate, sortModelQueryField, summsQueryField } from "consts/table";
import { getApiStateFromResponse } from "util/api";
import { compareIds } from "util/support";
import { emptySeenFilterValues } from "./state";
import type {
    SeenFilterValues,
    Transaction,
    TransactionResponse,
    TransactionsResponse,
    TransactionsState
} from "./types";

const criteriaKeyExcludedParams: string[] = [
    Paginate.page,
    Paginate.perPage,
    sortModelQueryField,
    summsQueryField
];

const cascadingFieldNames: Array<keyof SeenFilterValues> = [
    'currency',
    'status',
    'methodId',
    'via',
    'MOR',
    'merchantId',
    'PSP'
];

const rowFieldByFilterId: Record<keyof SeenFilterValues, string> = {
    currency: 'currency',
    status: 'status',
    methodId: 'methodId',
    via: 'via',
    MOR: 'MOR',
    merchantId: 'merchantId',
    PSP: 'PSP'
};

const buildCriteriaKey = (queryString: string) => {
    const params = new URLSearchParams(queryString);
    for (const key of criteriaKeyExcludedParams) {
        params.delete(key);
    }
    const entries = Array.from(params.entries())
        .map(([k, v]) => [k, v] as const)
        .sort(([a], [b]) => a.localeCompare(b));
    return new URLSearchParams(entries as any).toString();
};

const unionSeenValues = (
    previous: SeenFilterValues,
    rows: Transaction[]
): SeenFilterValues => {
    const next: { -readonly [K in keyof SeenFilterValues]: string[] } = {
        currency: [],
        status: [],
        methodId: [],
        via: [],
        MOR: [],
        merchantId: [],
        PSP: []
    };
    for (const field of cascadingFieldNames) {
        const seen = new Set<string>(previous[field]);
        const rowKey = rowFieldByFilterId[field];
        for (const row of rows) {
            const value = (row as unknown as Record<string, unknown>)[rowKey];
            if (value !== null && value !== undefined && value !== '') {
                seen.add(String(value));
            }
        }
        next[field] = Array.from(seen);
    }
    return next;
};

export const transactionsRequestLoading = (state: WritableDraft<TransactionsState>) => {
    state.tableLoadingState = ApiState.Pending;
};

export const transactionsRequestFulfilled = (
    state: WritableDraft<TransactionsState>,
    action: PayloadAction<
        ThunkReturnType<TransactionsResponse>,
        string,
        { arg: string }
    >
) => {
    state.tableLoadingState = ApiState.Succeeded;
    state.tableSlice = action.payload!.data;

    const criteriaKey = buildCriteriaKey(action.meta.arg ?? '');
    const rows = action.payload!.data?.data ?? [];
    const previousSeen = criteriaKey === state.seenFilterValuesCriteriaKey
        ? state.seenFilterValues
        : emptySeenFilterValues;

    state.seenFilterValuesCriteriaKey = criteriaKey;
    state.seenFilterValues = unionSeenValues(previousSeen, rows as Transaction[]);
};

export const transactionRequestFulfilled = (
    state: WritableDraft<TransactionsState>,
    { payload }: PayloadAction<ThunkReturnType<TransactionResponse>>
) => {
    state.transactionDetailsLoadingState = ApiState.Succeeded;

    const tableSlice = state.tableSlice;

    if (!tableSlice) {
        return;
    }

    state.tableSlice!.data = tableSlice.data.map(transactionEntity => {
        if (compareIds(transactionEntity.coreId, payload!.data.coreId)) {
            return {
                ...transactionEntity,
                ...payload!.data
            };
        }

        return transactionEntity;
    });
};

export const transactionsRequestRejected = (
    state: WritableDraft<TransactionsState>,
    { payload }: PayloadAction<unknown>
) => {
    state.tableLoadingState = getApiStateFromResponse(
        payload as ResourceResponse<null>,
        ApiState.Failed
    );
};
